import {
	Box,
	IconButton,
	Paper,
	TextField,
	Typography,
	Tooltip,
} from "@mui/material";
import { Close, RemoveCircleOutline } from "@mui/icons-material";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { Item } from "../../util/Item";
import { updateActiveTask, Util } from "../../util/Util";
import { canEditSalePrice, canSellBelowMinPrice, catalogMinSellPrice } from "../../util/salePrice";
import { MONEY_LIMITS, QTY_LIMITS, isWholeNumber, parseMoneyInput, qtyMin, roundQty } from "../../util/quantity";
import { useSale, SaleTask } from "./SaleContext";
import { useAuth } from "../../hooks/useAuth";
import { useUI } from "../../providers/MoleUIContext";
import QuantityStepperInput from "../../components/QuantityStepperInput";
import {
	isTillStockChecked,
	isNegativeStockAllowed,
	shouldEnforceStockQuantity,
} from "../../components/quantityStepperStock";
import { useAppSocket } from "../../providers/AppSocketContext";
import { toast } from "react-toastify";
import CachedItemImage from "../../components/CachedItemImage";
import PosAliasInput from "../../components/PosAliasInput";
import { useItemAliases } from "../../hooks/useItemAliases";
import { CatalogItemRowActions } from "../../components/CatalogItemActions";
import { canChangeLineQty, canRemoveLine } from '../../util/access';

interface Props {
	bundle?: any;
	/** A saved sale is being amended; otherwise this is a new sale. */
	amending?: boolean;
	setPendingTasks: Dispatch<SetStateAction<SaleTask<Item>[]>>;
}

const THUMB = 64;

/** Receipt / display name; click to edit when POS aliasing is enabled. */
const InlineItemName = ({
	row,
	canEdit,
	catalogItems,
	onCommit,
}: {
	row: Item;
	canEdit: boolean;
	catalogItems: Array<{ id: number; ItemName?: string; ItemCode?: string; SellingPrice?: number; Stock?: number; Category?: string; IsFractional?: boolean }>;
	onCommit: (next: Item) => void;
}) => {
	const { t } = useUI();
	const [editing, setEditing] = useState(false);
	const [draft, setDraft] = useState(row.item || "");
	const { data, loading } = useItemAliases(editing ? row.id : null);

	useEffect(() => {
		if (!editing) setDraft(row.item || "");
	}, [row.item, editing]);

	const applyName = (name: string, maybeCatalogId?: number) => {
		const trimmed = name.trim();
		if (!trimmed) {
			setDraft(row.item || "");
			setEditing(false);
			return;
		}

		// Match another catalog product by name → switch line identity (same as ItemTable)
		const catalogMatch =
			maybeCatalogId != null
				? catalogItems.find((c) => c.id === maybeCatalogId)
				: catalogItems.find((c) => Util.compareStrings(c.ItemName || "", trimmed));

		if (catalogMatch && catalogMatch.id !== row.id) {
			onCommit(
				new Item({
					...row,
					id: catalogMatch.id,
					item_code: catalogMatch.ItemCode ?? row.item_code,
					item: catalogMatch.ItemName ?? trimmed,
					price: catalogMatch.SellingPrice ?? row.price,
					stock: catalogMatch.Stock ?? row.stock,
					category: catalogMatch.Category ?? row.category,
					isFractional: catalogMatch.IsFractional === true,
					minSellPrice: catalogMinSellPrice(catalogMatch),
				}),
			);
		} else {
			onCommit(new Item({ ...row, item: trimmed }));
		}
		setEditing(false);
	};

	if (editing && canEdit) {
		return (
			<Box
				sx={{ flex: 1, minWidth: 0 }}
				onKeyDown={(e) => {
					if (e.key === "Escape") {
						e.preventDefault();
						e.stopPropagation();
						setDraft(row.item || "");
						setEditing(false);
					}
				}}
			>
				<PosAliasInput
					value={draft}
					onChange={setDraft}
					onCommit={(name) => applyName(name)}
					itemId={row.id}
					catalogName={data.catalog_name || row.item || ""}
					aliases={data.aliases}
					loading={loading}
					catalogItems={catalogItems}
					onCatalogMatch={(itemId, itemName) => applyName(itemName, itemId)}
					size="small"
					variant="filled"
					autoFocus
					onFocus={(e) => e.target.select()}
					label={t("common:ui.labels.receiptName")}
				/>
			</Box>
		);
	}

	return (
		<Box
			onClick={() => {
				if (canEdit) setEditing(true);
			}}
			role={canEdit ? "button" : undefined}
			tabIndex={canEdit ? 0 : undefined}
			onKeyDown={(e) => {
				if (canEdit && (e.key === "Enter" || e.key === " ")) {
					e.preventDefault();
					setEditing(true);
				}
			}}
			sx={{
				flex: 1,
				minWidth: 0,
				px: canEdit ? 0.75 : 0,
				py: canEdit ? 0.35 : 0,
				borderRadius: 1,
				cursor: canEdit ? "pointer" : "default",
				border: "1px solid transparent",
				"&:hover": canEdit
					? { bgcolor: "action.hover", borderColor: "divider" }
					: undefined,
			}}
			title={canEdit ? t("common:ui.labels.receiptName") : row.item}
		>
			<Typography
				variant="body2"
				fontWeight={700}
				sx={{
					lineHeight: 1.3,
					display: "-webkit-box",
					WebkitLineClamp: 2,
					WebkitBoxOrient: "vertical",
					overflow: "hidden",
				}}
			>
				{row.item}
			</Typography>
		</Box>
	);
};

/** Amount as text; click to edit (permissioned). */
const InlineAmount = ({
	price,
	canEdit,
	onCommit,
	label,
}: {
	price: number;
	canEdit: boolean;
	onCommit: (price: number) => void;
	label: string;
}) => {
	const [editing, setEditing] = useState(false);
	const [draft, setDraft] = useState(String(price));
	const inputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (!editing) setDraft(String(price));
	}, [price, editing]);

	useEffect(() => {
		if (editing) {
			inputRef.current?.focus();
			inputRef.current?.select();
		}
	}, [editing]);

	const finish = (save: boolean) => {
		if (save) {
			const v = parseMoneyInput(draft);
			if (v !== null) onCommit(v);
		} else {
			setDraft(String(price));
		}
		setEditing(false);
	};

	if (editing && canEdit) {
		return (
			<TextField
				size="small"
				type="number"
				label={label}
				variant="filled"
				fullWidth
				value={draft}
				inputRef={inputRef}
				onChange={(e) => setDraft(e.target.value)}
				onBlur={() => finish(true)}
				onKeyDown={(e) => {
					if (e.key === "Enter") {
						e.preventDefault();
						finish(true);
					} else if (e.key === "Escape") {
						e.preventDefault();
						finish(false);
					}
				}}
				inputProps={{ min: 0, max: MONEY_LIMITS.max, step: 0.01 }}
			/>
		);
	}

	return (
		<Box
			onClick={() => {
				if (canEdit) setEditing(true);
			}}
			role={canEdit ? "button" : undefined}
			tabIndex={canEdit ? 0 : undefined}
			onKeyDown={(e) => {
				if (canEdit && (e.key === "Enter" || e.key === " ")) {
					e.preventDefault();
					setEditing(true);
				}
			}}
			sx={{
				minHeight: 40,
				px: 1,
				py: 0.5,
				borderRadius: 1,
				display: "flex",
				alignItems: "center",
				justifyContent: "flex-start",
				cursor: canEdit ? "pointer" : "default",
				border: "1px solid transparent",
				"&:hover": canEdit
					? { bgcolor: "action.hover", borderColor: "divider" }
					: undefined,
			}}
			title={canEdit ? label : undefined}
		>
			<Typography variant="body2" fontWeight={700} color="text.primary">
				{Util.formatNumber(price)}
			</Typography>
		</Box>
	);
};

const PosCartList = ({ bundle, amending = false, setPendingTasks }: Props) => {
	const { activeTask, items: catalog } = useSale();
	const { access } = useAuth();
	const { appSettings } = useAppSocket();
	const { t } = useUI();

	const rows = activeTask?.items ?? [];
	// Line rights hold under both verbs (F-SAL-19): create on a new sale,
	// update when amending a saved one.
	const lineAction = amending ? "update" : "create";
	const canEditQty = canChangeLineQty(access, lineAction);
	const canEditPrice = canEditSalePrice(access, lineAction);
	const canDelete = canRemoveLine(access, lineAction);
	const allowPosAliasing = Util.isBoolSettingEnabled(appSettings?.ALLOW_POS_ALIASING?.value);
	const allowNegative = isNegativeStockAllowed(appSettings?.ALLOW_NEGATIVE_STOCK?.value);

	const replaceLine = (uniqueId: string, next: Item) => {
		updateActiveTask(setPendingTasks, (prev) => ({
			items: (prev.items ?? []).map((row) =>
				row.uniqueId === uniqueId ? next : row,
			),
		}));
	};

	const removeLine = (uniqueId: string) => {
		updateActiveTask(setPendingTasks, (prev) => ({
			items: (prev.items ?? []).filter((row) => row.uniqueId !== uniqueId),
		}));
	};

	const quantityMaxFor = (row: Item) => {
		const catalogItem = catalog.find((c: any) => c.id === row.id);
		const metered = isTillStockChecked(catalogItem);
		if (!shouldEnforceStockQuantity({ isMetered: metered, allowNegativeStock: allowNegative })) {
			return QTY_LIMITS.max;
		}
		const stock = Number(catalogItem?.Stock ?? row.stock ?? 0);
		const bundleQty = bundle
			? bundle.reduce((sum: number, b: any) => (
				parseInt(b.item_id, 10) === row.id ? sum + b.quantity : sum
			), 0)
			: 0;
		const otherQty = rows.reduce((sum, r) => (
			r.id === row.id && r.uniqueId !== row.uniqueId ? sum + r.quantity : sum
		), 0);
		const available = roundQty(stock + bundleQty - otherQty);
		return Math.min(QTY_LIMITS.max, Math.max(available, qtyMin(!!row.isFractional)));
	};

	const onQtyChange = (row: Item, quantity: number) => {
		if (quantity < qtyMin(!!row.isFractional) || quantity > QTY_LIMITS.max) {
			toast.error(t("common:validation.quantityRange", {
				max: Util.formatNumber(QTY_LIMITS.max),
			}));
			return;
		}
		if (!row.isFractional && !isWholeNumber(quantity)) {
			toast.error(t("common:validation.quantityMustBeWhole"));
			return;
		}
		const max = quantityMaxFor(row);
		if (quantity > max) {
			toast.error(t("common:validation.insufficientStockAvailable", {
				available: Util.formatNumber(max),
			}));
			return;
		}
		replaceLine(row.uniqueId, new Item({ ...row, quantity }));
	};

	const onPriceChange = (row: Item, price: number) => {
		if (Number.isNaN(price) || price < 0 || price > MONEY_LIMITS.max) {
			toast.error(t("common:validation.amountRange", {
				max: Util.formatNumber(MONEY_LIMITS.max),
			}));
			return;
		}
		if (!canSellBelowMinPrice(access, lineAction)) {
			const catalogItem = catalog.find((c: any) => c.id === row.id);
			const minSell = catalogMinSellPrice(catalogItem, row);
			if (minSell > 0 && price < minSell) {
				toast.error(t("common:validation.priceBelowMinSell", {
					min: Util.formatNumber(minSell),
					item: row.item,
				}));
				return;
			}
		}
		replaceLine(row.uniqueId, new Item({ ...row, price }));
	};

	if (rows.length === 0) {
		return (
			<Paper
				elevation={0}
				variant="outlined"
				sx={{
					p: 2.5,
					textAlign: "center",
					borderStyle: "dashed",
					color: "text.secondary",
				}}
			>
				<Typography variant="body2">
					{t("common:pos.cart.empty")}
				</Typography>
			</Paper>
		);
	}

	return (
		<Box sx={{ width: "100%" }}>
			<Box
				sx={{
					display: "grid",
					gridTemplateColumns: {
						xs: "1fr",
						sm: "repeat(2, 1fr)",
						md: "repeat(3, 1fr)",
					},
					gap: 1,
					alignItems: "stretch",
				}}
			>
				{rows.map((row, index) => (
					<Paper
						key={row.uniqueId}
						elevation={0}
						variant="outlined"
						sx={{
							p: 1.25,
							borderRadius: 2,
							display: "flex",
							flexDirection: "column",
							gap: 1,
							bgcolor: "background.paper",
							minWidth: 0,
							"&:hover": { borderColor: "primary.light" },
							"& .catalog-item-option-actions": {
								opacity: 0,
								pointerEvents: "none",
							},
							"&:hover .catalog-item-option-actions, & .catalog-item-option-actions--open": {
								opacity: 1,
								pointerEvents: "auto",
							},
						}}
					>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
							<Typography
								variant="body2"
								sx={{
									flexShrink: 0,
									fontWeight: 800,
									color: "primary.main",
									letterSpacing: 0.2,
									lineHeight: 1,
									minWidth: "1.75rem",
								}}
							>
								#{index + 1}
							</Typography>
							<Box
								sx={{
									width: THUMB,
									height: THUMB,
									flexShrink: 0,
									borderRadius: 1.5,
									overflow: "hidden",
									border: "1px solid",
									borderColor: "divider",
								}}
							>
								<CachedItemImage itemId={row.id} alt={row.item} lazy />
							</Box>
							<InlineItemName
								row={row}
								canEdit={allowPosAliasing}
								catalogItems={catalog}
								onCommit={(next) => replaceLine(row.uniqueId, next)}
							/>
							<CatalogItemRowActions itemId={row.id} itemName={row.item} isPos={true} />
							{canDelete && (
								<Tooltip title={t("common:ui.tooltips.delete")}>
									<IconButton
										size="small"
										color="error"
										tabIndex={-1}
										onClick={() => removeLine(row.uniqueId)}
										aria-label={t("common:ui.tooltips.delete")}
										sx={{ flexShrink: 0 }}
									>
										<RemoveCircleOutline fontSize="small" />
									</IconButton>
								</Tooltip>
							)}
						</Box>


						<Box
							sx={{
								height: 0,
								border: 0,
								borderTop: "2px solid",
								borderColor: "primary.main",
								opacity: 0.85,
								mx: 0.25,
							}}
						/>

						<Box
							sx={{
								display: "grid",
								gridTemplateColumns: "1fr auto 1fr auto",
								gap: 0.5,
								alignItems: "center",
							}}
						>
							<QuantityStepperInput
								size="small"
								compact
								fullWidth
								label={t("common:tables.quantity")}
								value={row.quantity}
								min={qtyMin(!!row.isFractional)}
								max={quantityMaxFor(row)}
								disabled={!canEditQty}
								fractional={row.isFractional}
								onChange={(v) => onQtyChange(row, v)}
							/>
							<Close
								fontSize="small"
								aria-hidden
								sx={{
									color: "text.secondary",
									opacity: 0.75,
									mx: 0.25,
									alignSelf: "center",
									mt: 1.5,
								}}
							/>
							<InlineAmount
								price={row.price}
								canEdit={canEditPrice}
								label={t("common:tables.amount")}
								onCommit={(price) => onPriceChange(row, price)}
							/>
							<Typography
								variant="body2"
								fontWeight={800}
								color="primary.main"
								sx={{ whiteSpace: "nowrap", pl: 0.5, mt: 1.5 }}
							>
								{Util.formatNumber(row.total)}
							</Typography>
						</Box>
					</Paper>
				))}
			</Box>
		</Box>
	);
};

export default PosCartList;
